/* Wishlist Plus runs SDK-only (Swym draws no UI), so this drives all wishlist UI. It starts
 * from window.SwymCallbacks, Swym's entry point, which hands over `swat` once the embed loads. */
(() => {
  const BUTTON = '[data-wishlist-button]';
  const PANEL = '[data-wishlist-panel]';
  const state = { swat: null, lid: null, items: [], count: 0 };

  const isSaved = (button) =>
    state.items.some(
      (item) => String(item.empi) === button.dataset.product || String(item.epi) === button.dataset.variant
    );

  // Several variants of one product may be saved; show and count it once.
  function savedProducts() {
    const seen = new Set();
    return [...state.items]
      .sort((a, b) => (b.cts || 0) - (a.cts || 0))
      .filter((item) => {
        const key = String(item.empi);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  // Last known count per customer, so the header shows it straight away
  // instead of waiting for Swym (the markup starts empty, not "0").
  const COUNT_KEY = `wishlist-count:${window.ShopifyAnalytics?.meta?.page?.customerId || 'guest'}`;

  // Writes only on change: textContent always swaps the text node, which the
  // MutationObserver below would see and answer with another render.
  function renderCount(count) {
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      if (el.textContent !== String(count)) el.textContent = count;
    });
  }

  // DOM-stable, so the page-wide MutationObserver can call it freely.
  function renderHearts() {
    document.querySelectorAll(BUTTON).forEach((button) => {
      const pressed = isSaved(button) ? 'true' : 'false';
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    });
    renderCount(state.count);
    try {
      localStorage.setItem(COUNT_KEY, String(state.count));
    } catch {}
  }

  function render() {
    renderHearts();
    renderPanel();
  }

  // Swym tags wishlists lty "wl" (older builds leave it unset); skip other list
  // types such as save-for-later.
  const isWishlist = (list) => !list.lty || list.lty === 'wl';

  function refresh() {
    return new Promise((resolve) => {
      const done = (lists) => {
        const wishlists = (Array.isArray(lists) ? lists : []).filter(isWishlist);
        state.lid = state.lid || wishlists[0]?.lid || null;
        state.items = wishlists.flatMap((list) => list.listcontents || []);
        state.count = savedProducts().length;
        render();
        resolve();
      };
      const fail = (error) => {
        console.error('Wishlist Plus: could not fetch lists', error);
        resolve();
      };
      // The docs name the error callback both errorFn and onError; pass both.
      state.swat.fetchLists({ callbackFn: done, errorFn: fail, onError: fail });
    });
  }

  function ensureList() {
    if (state.lid) return Promise.resolve(state.lid);
    return new Promise((resolve, reject) => {
      state.swat.createList(
        { lname: 'My Wishlist' },
        (list) => {
          state.lid = list.lid;
          resolve(list.lid);
        },
        reject
      );
    });
  }

  function removeProduct(productId) {
    const entries = state.items.filter((entry) => String(entry.empi) === String(productId));
    state.items = state.items.filter((entry) => !entries.includes(entry));
    state.count = savedProducts().length;
    render();
    const removals = entries.map(
      (entry) =>
        new Promise((resolve) => {
          const product = { epi: Number(entry.epi), empi: Number(entry.empi), du: entry.du };
          state.swat.deleteFromList(entry.lid || state.lid, product, resolve, (error) => {
            console.error('Wishlist Plus: could not update the wishlist', error);
            resolve();
          });
        })
    );
    return Promise.all(removals).then(refresh);
  }

  function toggle(button) {
    const saved = button.getAttribute('aria-pressed') === 'true';
    const product = {
      epi: Number(button.dataset.variant),
      empi: Number(button.dataset.product),
      du: button.dataset.url,
    };
    button.setAttribute('aria-busy', 'true');
    const settle = () => {
      button.removeAttribute('aria-busy');
      return refresh();
    };
    const fail = (error) => {
      console.error('Wishlist Plus: could not update the wishlist', error);
      settle();
    };

    if (saved) {
      removeProduct(button.dataset.product).then(() => button.removeAttribute('aria-busy'));
    } else {
      state.items = [...state.items, { ...product, lid: state.lid, cts: Date.now() }];
      state.count = savedProducts().length;
      render();
      ensureList()
        .then((lid) => state.swat.addToList(lid, product, settle, fail))
        .catch(fail);
    }
  }

  const rowCache = new Map();
  let renderToken = 0;

  const handleFromUrl = (url) => {
    try {
      const match = new URL(url, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  };

  function fetchRow(item) {
    const handle = handleFromUrl(item.du);
    if (!handle) return Promise.resolve(null);
    if (!rowCache.has(handle)) {
      const root = (window.routes?.root_url || '/').replace(/\/$/, '');
      rowCache.set(
        handle,
        fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=wishlist-item`)
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => '')
      );
    }
    return rowCache.get(handle).then((html) => {
      const row = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-wishlist-item]');
      return row ? document.importNode(row, true) : null;
    });
  }

  // Only fills the panel while it's showing; switching to the tab fills it.
  function renderPanel() {
    const panel = document.querySelector(PANEL);
    if (!panel || panel.hidden || panel.dataset.state === 'guest') return;
    if (!state.swat) {
      panel.dataset.state = 'loading';
      return;
    }
    const list = panel.querySelector('[data-wishlist-items]');
    const products = savedProducts();
    const token = ++renderToken;

    const ids = new Set(products.map((item) => String(item.empi)));
    list.querySelectorAll('[data-wishlist-item]').forEach((row) => {
      if (!ids.has(row.dataset.productId)) row.remove();
    });
    if (!products.length) {
      panel.dataset.state = 'empty';
      return;
    }
    if (!list.children.length) panel.dataset.state = 'loading';

    Promise.all(products.map(fetchRow)).then((rows) => {
      if (token !== renderToken) return;
      // Reuse on-screen rows and only move one when the order changed: moving a
      // row closes an open <select> and loses a picked size.
      const existing = new Map([...list.children].map((row) => [row.dataset.productId, row]));
      const next = rows.filter(Boolean).map((row) => existing.get(row.dataset.productId) || row);
      [...list.children].forEach((row) => {
        if (!next.includes(row)) row.remove();
      });
      next.forEach((row, index) => {
        if (list.children[index] !== row) list.insertBefore(row, list.children[index] || null);
      });
      panel.dataset.state = next.length ? 'ready' : 'empty';
    });
  }

  function addToBag(form) {
    const error = form.querySelector('[data-wishlist-error]');
    const select = form.querySelector('select[name="id"]');
    if (select && !select.value) {
      error.textContent = window.wishlistStrings?.selectOption || '';
      error.hidden = false;
      select.focus();
      return;
    }
    error.hidden = true;

    const cart = document.querySelector('cart-items');
    const body = new FormData(form);
    body.append('quantity', '1');
    if (cart) {
      body.append('sections', cart.getSectionsToRender().map((section) => section.section));
      body.append('sections_url', window.location.pathname);
    }
    const submit = form.querySelector('[type="submit"]');
    submit?.setAttribute('aria-disabled', 'true');

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          error.textContent = response.description || response.message || '';
          error.hidden = false;
          return;
        }
        document.dispatchEvent(
          new CustomEvent('cart:update', { detail: { source: 'wishlist', productVariantId: body.get('id') } })
        );
        const row = form.closest('[data-wishlist-item]');
        if (row && state.swat) removeProduct(row.dataset.productId);
        // Re-renders the bag and re-opens the drawer, which flips it to the
        // Bag tab (the drawer's tabs follow dialog:trigger:cart-drawer).
        if (cart) cart.renderContents(response);
        else window.location = window.routes.cart_url;
      })
      .catch(() => {
        error.textContent = window.cartStrings?.error || '';
        error.hidden = false;
      })
      .finally(() => submit?.removeAttribute('aria-disabled'));
  }

  document.addEventListener('cart-drawer:tab', (event) => {
    if (event.detail?.tab === 'wishlist') renderPanel();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-wishlist-add]');
    if (!form) return;
    event.preventDefault();
    addToBag(form);
  });

  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-wishlist-add]');
    if (form) form.querySelector('[data-wishlist-error]').hidden = true;
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest(BUTTON);
    if (button) {
      event.preventDefault();
      if (state.swat && !button.hasAttribute('aria-busy')) toggle(button);
      return;
    }
    const remove = event.target.closest('[data-wishlist-remove]');
    if (remove && state.swat) {
      const row = remove.closest('[data-wishlist-item]');
      if (row) removeProduct(row.dataset.productId);
    }
  });

  document.addEventListener('variant:change', (event) => {
    const { sectionId, variant } = event.detail || {};
    if (!variant?.id) return;
    const scope = (sectionId && document.querySelector(`product-page[data-section="${sectionId}"]`)) || document;
    scope.querySelectorAll(BUTTON).forEach((button) => {
      button.dataset.variant = variant.id;
    });
    renderHearts();
  });

  // Picks up hearts added later (infinite scroll, quick add); hearts only, never the drawer panel.
  let pending = false;
  new MutationObserver(() => {
    if (pending || !state.swat) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      renderHearts();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  try {
    const cached = localStorage.getItem(COUNT_KEY);
    if (cached !== null) renderCount(cached);
  } catch {}

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push((swat) => {
    state.swat = swat;
    if (swat.evtLayer && swat.JSEvents) {
      swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, refresh);
      swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, refresh);
    }
    refresh();
  });
})();
